/*
 * Elexa cart (group B). Extends the foundation's Elexa.cart pipeline (assets/elexa-core.js).
 *
 *  - Gift reconciliation after every successful cart change (afterChange hook), on page load and
 *    after cart changes made by Dawn code: threshold gift add/remove, linked gifts (parent removed
 *    or quantity changed), rely-on product, protection quantity, orphan protection/gift lines.
 *    Follow-up requests run inside the same queued task, so one user action = one reconciliation
 *    and never a loop.
 *  - Drawer renderer for the `cart-drawer` section: swaps the panel, keeps scroll, focus trap,
 *    then animates the progress bar from the previous value.
 *  - Behaviours (delegated, survive every re-render): shipping protection toggle, discount code
 *    form + applied-code pills (per-code applicable state), upsell variant menus + add, cart page
 *    protection row, editor helpers.
 *
 * Reads the server state from #ExCartState (snippets/cart-drawer.liquid). Docs: B-cart MANIFEST.
 */
(() => {
  const Elexa = window.Elexa;
  if (!Elexa || !Elexa.cart || Elexa.cartExtended) return;
  Elexa.cartExtended = true;

  const C = Elexa.cart;
  const routes = (Elexa.config && Elexa.config.routes) || {};
  const SUPPRESS_SELECTOR = '[data-ex-suppress-threshold-gift]';

  /* ------------------------------------------------------------------ *
   * State
   * ------------------------------------------------------------------ */
  const readState = (root = document) => {
    const el = root.querySelector('#ExCartState') || root.querySelector('[data-ex-cart-state]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };
  let state = readState() || {};
  let lastQualifying = typeof state.qualifyingCents === 'number' ? state.qualifyingCents : 0;
  let lastExternalRender = 0;

  const str = (key, fallback = '') => (state.strings && state.strings[key]) || fallback;
  const drawerEl = () => document.querySelector('cart-drawer');

  C.state = () => state;
  C.markExternalRender = () => {
    lastExternalRender = Date.now();
  };

  /* ------------------------------------------------------------------ *
   * Money and goals
   * ------------------------------------------------------------------ */
  const stripTags = (html) => String(html).replace(/<[^>]*>/g, '');
  const moneyNoZeros = (cents) => {
    let out = stripTags(Elexa.money(cents));
    if (Math.round(cents) % 100 === 0) out = out.replace(/([.,])00(?!\d)/, '');
    return out;
  };
  const rate = () => {
    const r = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    return r > 0 ? r : 1;
  };
  /** Goals in the cart currency (minor units). Overrides are resolved server-side. */
  function goals(st = state) {
    if (!st) return { shipping: 0, gift: 0 };
    if (!st.needsConvert) return { shipping: st.shippingCents || 0, gift: st.giftCents || 0 };
    const r = rate();
    return { shipping: Math.round((st.baseShippingCents || 0) * r), gift: Math.round((st.baseGiftCents || 0) * r) };
  }
  C.goals = () => goals();

  /* ------------------------------------------------------------------ *
   * Lines + reconciliation plan
   * ------------------------------------------------------------------ */
  function linesFromCart(cart, st = state) {
    const protVariant = Number(st.protectionVariantIdMatch || 0);
    return (cart.items || []).map((i) => {
      const p = i.properties || {};
      const gift = p._elexa_gift ? String(p._elexa_gift) : '';
      return {
        key: i.key,
        variant: Number(i.variant_id),
        product: Number(i.product_id),
        qty: Number(i.quantity),
        final: Number(i.final_line_price),
        price: Number(i.original_price),
        gift,
        parent: p._elexa_gift_parent ? String(p._elexa_gift_parent) : '',
        giftQty: p._elexa_gift_qty ? String(p._elexa_gift_qty) : '',
        protection: !gift && (Boolean(p._elexa_protection) || (protVariant > 0 && Number(i.variant_id) === protVariant)),
      };
    });
  }

  function qualifying(lines, totalCents) {
    let v = Number(totalCents) || 0;
    lines.forEach((l) => {
      if (l.protection || l.gift || l.final === 0) v -= l.final;
    });
    return Math.max(0, v);
  }

  /**
   * Compute one reconciliation batch. Returns { updates: {key: qty}, add: [items], fade: [keys] }
   * or null when the cart is already consistent.
   */
  function plan(lines, totalCents, st = state) {
    const updates = {};
    const add = [];
    const fade = [];
    const set = (key, qty, leave) => {
      updates[key] = qty;
      if (leave && qty === 0) fade.push(key);
    };
    const zeroed = (l) => updates[l.key] === 0;
    const giftList = (st.giftProductIds || []).map(Number);
    const isReal = (l) => !l.protection && !l.gift && !(l.final === 0 && giftList.includes(l.product));

    const qtyByProduct = {};
    lines.filter(isReal).forEach((l) => {
      qtyByProduct[l.product] = (qtyByProduct[l.product] || 0) + l.qty;
    });

    // 1. Linked gifts follow their parent product (both directions).
    lines
      .filter((l) => l.gift === 'linked')
      .forEach((l) => {
        const parentQty = qtyByProduct[Number(l.parent)] || 0;
        if (!parentQty) {
          set(l.key, 0, true);
        } else {
          const target = /^(one|1)$/i.test(l.giftQty) ? 1 : parentQty;
          if (l.qty !== target) set(l.key, target);
        }
      });

    // 2. Rely-on product: when it leaves, gift-list gifts without a parent leave too.
    const relyOn = Number(st.relyOnProductId || 0);
    if (relyOn && !qtyByProduct[relyOn]) {
      lines.forEach((l) => {
        if (zeroed(l) || l.gift === 'threshold' || l.parent || l.protection) return;
        if (giftList.includes(l.product) && (l.gift || l.final === 0 || l.price === 0)) set(l.key, 0, true);
      });
    }

    // 3. Shipping protection: one line, quantity 1.
    lines
      .filter((l) => l.protection && !zeroed(l))
      .forEach((l, i) => {
        if (i > 0) set(l.key, 0);
        else if (l.qty > 1) set(l.key, 1);
      });

    // 4. Orphans: only protection and/or gift lines left -> empty the cart.
    const remaining = lines.filter((l) => !zeroed(l));
    if (remaining.length && !remaining.some(isReal)) {
      remaining.forEach((l) => set(l.key, 0, true));
    } else {
      // 5. Threshold gift (progress bar).
      const { gift: G } = goals(st);
      const giftVariant = Number(st.giftVariantId || 0);
      const V = qualifying(lines, totalCents);
      const giftLines = lines.filter((l) => l.gift === 'threshold');
      const eligible = G > 0 && giftVariant > 0 && st.giftAvailable !== false && V >= G;
      if (eligible) {
        const keep = giftLines.find((l) => l.variant === giftVariant);
        giftLines.forEach((l) => {
          if (l !== keep) set(l.key, 0);
        });
        if (keep) {
          if (keep.qty !== 1) set(keep.key, 1);
        } else if (!document.querySelector(SUPPRESS_SELECTOR)) {
          add.push({ id: giftVariant, quantity: 1, properties: { _elexa_gift: 'threshold' } });
        }
      } else {
        giftLines.forEach((l) => set(l.key, 0, true));
      }
    }

    return Object.keys(updates).length || add.length ? { updates, add, fade } : null;
  }
  C.planReconcile = (cart) => plan(linesFromCart(cart), cart.total_price);

  /* ------------------------------------------------------------------ *
   * Busy state (checkout disabled while reconciliation runs)
   * ------------------------------------------------------------------ */
  let busyCount = 0;
  function setBusy(on) {
    busyCount = Math.max(0, busyCount + (on ? 1 : -1));
    document.documentElement.toggleAttribute('data-ex-cart-busy', busyCount > 0);
  }
  document.addEventListener(
    'click',
    (e) => {
      if (!busyCount) return;
      if (e.target.closest('[name="checkout"], .cart__checkout-button')) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true
  );

  function fadeRows(keys) {
    if (!keys || !keys.length) return;
    keys.forEach((key) => {
      document.querySelectorAll(`[data-line-key="${CSS.escape(key)}"]`).forEach((row) => row.classList.add('ex-line--leaving'));
    });
  }

  const sectionsBody = () => ({ sections: C.sectionIds().join(','), sections_url: window.location.pathname });

  /** Run a plan inside the current queued task. Resolves the latest full cart (or null). */
  async function execute(p) {
    if (!p) return null;
    setBusy(true);
    let latest = null;
    try {
      fadeRows(p.fade);
      if (Object.keys(p.updates).length) {
        const res = await C.request(`${routes.cartUpdate}.js`, Object.assign({ updates: p.updates }, sectionsBody()));
        latest = res;
        C.lastCart = res;
        C.render(res.sections, { seq: ++C.seq, cart: res, action: 'reconcile' });
      }
      if (p.add.length) {
        try {
          const res = await C.request(`${routes.cartAdd}.js`, Object.assign({ items: p.add }, sectionsBody()));
          latest = await C.get();
          C.render(res.sections, { seq: ++C.seq, cart: latest, action: 'reconcile' });
        } catch (e) {
          // Gift sold out or not addable: skip silently (spec: no visible error).
        }
      }
    } catch (e) {
      console.warn('[Elexa cart] reconciliation failed', e);
    } finally {
      setBusy(false);
    }
    return latest;
  }

  const replaceInPlace = (target, source) => {
    if (!target || !source || target === source) return;
    Object.keys(target).forEach((k) => delete target[k]);
    Object.assign(target, source);
  };

  // After every pipeline mutation (add / change / update / refresh).
  C.hooks.afterChange.push(async (payload) => {
    const { action, cart, options = {} } = payload;
    if (options.reconcile === false || action === 'reconcile') return;
    if (action === 'external') {
      onExternal();
      return;
    }
    if (!cart || !Array.isArray(cart.items)) return;
    const latest = await execute(plan(linesFromCart(cart), cart.total_price));
    if (latest) replaceInPlace(cart, latest);
  });

  // Cart changed by Dawn code (product form, quick add, quick order list): reconcile, else sync UI.
  function onExternal() {
    C.enqueue(async () => {
      let fresh;
      try {
        fresh = await C.get();
      } catch (e) {
        return;
      }
      const latest = await execute(plan(linesFromCart(fresh), fresh.total_price));
      if (latest) {
        C.announce('reconcile', latest, 'reconcile', null, { reconcile: false });
        return;
      }
      if (Date.now() - lastExternalRender > 3000) {
        try {
          const ids = C.sectionIds();
          const res = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids.join(','))}`, {
            headers: { Accept: 'application/json' },
          });
          if (res.ok) C.render(await res.json(), { seq: ++C.seq, cart: fresh, action: 'sync' });
        } catch (e) {
          /* keep current UI */
        }
      }
    }).catch(() => {});
  }

  // Page load: reconcile once from the server-rendered state (no extra request when consistent).
  function initialReconcile() {
    if (!Array.isArray(state.lines) || !state.lines.length) return;
    const p = plan(state.lines, state.totalCents);
    if (!p) return;
    C.enqueue(async () => {
      const latest = await execute(p);
      if (latest) C.announce('reconcile', latest, 'reconcile', null, { reconcile: false });
    }).catch(() => {});
  }

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */
  const FOCUS_MARKERS = ['data-ex-protection-toggle', 'data-ex-upsell-add', 'data-ex-discount-input', 'data-ex-discount-submit'];

  function focusDescriptor(root) {
    const a = document.activeElement;
    if (!a || a === document.body || !root || !root.contains(a)) return null;
    const line = a.closest('[data-line-key]');
    return {
      key: line ? line.dataset.lineKey : null,
      name: a.getAttribute('name'),
      marker: FOCUS_MARKERS.find((m) => a.hasAttribute(m)) || null,
      id: a.id || null,
    };
  }

  function focusTarget(root, d) {
    if (!d || !root) return null;
    let el = null;
    if (d.key) {
      const line = root.querySelector(`[data-line-key="${CSS.escape(d.key)}"]`);
      if (line && d.name) el = line.querySelector(`[name="${CSS.escape(d.name)}"]`);
    }
    if (!el && d.marker) el = root.querySelector(`[${d.marker}]`);
    if (!el && d.id) el = document.getElementById(d.id);
    if (!el && d.key) el = root.querySelector('.cart-item__name');
    return el;
  }

  let pendingFocus = null;

  // Section Rendering payload for the drawer section (all cart types).
  C.renderers['cart-drawer'] = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = readState(doc);
    lastQualifying = typeof state.qualifyingCents === 'number' ? state.qualifyingCents : lastQualifying;
    if (next) {
      state = next;
      const el = document.getElementById('ExCartState');
      if (el) el.textContent = JSON.stringify(next);
    }

    const drawer = drawerEl();
    if (drawer) {
      const target = drawer.querySelector('.drawer__inner');
      const source = doc.querySelector('#CartDrawer .drawer__inner');
      if (target && source) {
        pendingFocus = focusDescriptor(target);
        const body = target.querySelector('[data-ex-cart-body]');
        const scroll = body ? body.scrollTop : 0;
        const note = target.querySelector('#Details-CartDrawer');
        const noteOpen = Boolean(note && note.open);
        target.innerHTML = source.innerHTML;
        target.className = source.className;
        const nextBody = target.querySelector('[data-ex-cart-body]');
        if (nextBody) nextBody.scrollTop = scroll;
        const nextNote = target.querySelector('#Details-CartDrawer');
        if (nextNote && noteOpen) nextNote.open = true;
      }
      if (next) drawer.classList.toggle('is-empty', next.itemCount === 0);
    }

    // Upsells inside Dawn's add-to-cart popup (cart type "notification").
    const notifTarget = document.getElementById('cart-notification-upsells');
    if (notifTarget) {
      const tpl = doc.getElementById('ExCartNotificationUpsells');
      notifTarget.innerHTML = tpl ? tpl.innerHTML : '';
    }
  };

  function restoreDrawerFocus() {
    const drawer = drawerEl();
    if (!drawer || !drawer.classList.contains('active') || typeof window.trapFocus !== 'function') {
      pendingFocus = null;
      return;
    }
    const empty = drawer.classList.contains('is-empty');
    const container = empty ? drawer.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
    if (!container) return;
    const target = focusTarget(container, pendingFocus);
    if (pendingFocus || target) {
      window.trapFocus(container, target || (empty ? container.querySelector('a') : drawer.querySelector('.drawer__inner')));
    } else {
      window.trapFocus(container, drawer.querySelector('.drawer__inner'));
    }
    pendingFocus = null;
  }

  /* ---------- progress bar ---------- */
  function progressMessage(bar, V, S, G) {
    const d = bar.dataset;
    let kind;
    let missing = 0;
    const all = (!(S > 0) || V >= S) && (!(G > 0) || V >= G);
    if (all) kind = d.tAll ? 'tAll' : S > 0 ? 'tShipEarned' : 'tGiftEarned';
    else if (S > 0 && G > 0) {
      if (S === G || (S < G && V >= S) || (G < S && V < G)) {
        kind = 'tGiftAway';
        missing = G - V;
      } else {
        kind = 'tShipAway';
        missing = S - V;
      }
    } else if (S > 0) {
      kind = 'tShipAway';
      missing = S - V;
    } else {
      kind = 'tGiftAway';
      missing = G - V;
    }
    return String(d[kind] || '')
      .split('[missingAmount]')
      .join(moneyNoZeros(Math.max(0, missing)))
      .split('[freeProduct]')
      .join(d.giftName || '');
  }

  function updateProgress(animateFrom) {
    // .ex-pb only: carousel controls use [data-ex-progress] too, and an inline width would freeze them.
    document.querySelectorAll('.ex-pb[data-ex-progress]').forEach((bar) => {
      const V = Number(bar.dataset.value) || 0;
      let S = Number(bar.dataset.shipping) || 0;
      let G = Number(bar.dataset.gift) || 0;
      const milestones = Array.from(bar.querySelectorAll('[data-ex-milestone]'));
      if (bar.dataset.needsConvert === 'true') {
        const g = goals();
        S = g.shipping;
        G = g.gift;
        milestones.forEach((m) => {
          m.dataset.threshold = m.dataset.exMilestone === 'shipping' ? S : G;
        });
        const msg = progressMessage(bar, V, S, G);
        const msgEl = bar.querySelector('[data-ex-progress-msg]');
        if (msgEl) msgEl.textContent = msg;
        const area = bar.querySelector('[role="progressbar"]');
        if (area) area.setAttribute('aria-valuetext', msg);
      }
      const max = Math.max(S, G);
      const fill = bar.querySelector('[data-ex-progress-fill]');
      const area = bar.querySelector('[role="progressbar"]');
      const pct = (v) => (max > 0 ? Math.min(100, (Math.max(0, v) * 100) / max) : 0);
      const paint = (v) => {
        if (fill) fill.style.width = `${pct(v)}%`;
        milestones.forEach((m) => m.classList.toggle('is-achieved', v >= Number(m.dataset.threshold)));
      };
      if (area) area.setAttribute('aria-valuenow', Math.round(pct(V)));
      if (typeof animateFrom !== 'number' || animateFrom === V || Elexa.reducedMotion()) {
        paint(V);
        return;
      }
      const start = performance.now();
      paint(animateFrom);
      const step = (now) => {
        const t = Math.min(1, (now - start) / 1000);
        const eased = 1 - (1 - t) * (1 - t); // ease-out quad
        paint(animateFrom + (V - animateFrom) * eased);
        if (t < 1 && bar.isConnected) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }

  /* ---------- discount pills ---------- */
  const TAG_SVG =
    '<svg class="ex-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r=".5" fill="currentColor"/></svg>';
  const X_SVG =
    '<svg class="ex-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';

  function renderCodes(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) return;
    document.querySelectorAll('[data-ex-discount-codes]').forEach((list) => {
      list.textContent = '';
      cart.discount_codes.forEach(({ code, applicable }) => {
        if (!code) return;
        const li = document.createElement('li');
        li.className = `ex-discount__code${applicable ? '' : ' is-inapplicable'}`;
        li.dataset.code = code;
        li.innerHTML = `${TAG_SVG}<span class="ex-discount__code-text"></span>${
          applicable ? '' : '<span class="ex-discount__code-state"></span>'
        }<button type="button" class="ex-discount__remove">${X_SVG}</button>`;
        li.querySelector('.ex-discount__code-text').textContent = code;
        const st = li.querySelector('.ex-discount__code-state');
        if (st) st.textContent = str('notApplicable', 'Not applicable');
        const btn = li.querySelector('button');
        btn.dataset.exDiscountRemove = code;
        btn.setAttribute('aria-label', str('removeCode', 'Remove [code]').replace('[code]', code));
        list.appendChild(li);
      });
    });
  }

  function appliedCodes() {
    if (C.lastCart && Array.isArray(C.lastCart.discount_codes)) return C.lastCart.discount_codes.map((d) => d.code);
    return state.appliedCodes || [];
  }

  /* ---------- upsells ---------- */
  function syncUpsell(card) {
    const data = card.querySelector('[data-ex-upsell-variants]');
    const btn = card.querySelector('[data-ex-upsell-add]');
    if (!data || !btn) return;
    let variants;
    try {
      variants = JSON.parse(data.textContent);
    } catch (e) {
      return;
    }
    const selects = Array.from(card.querySelectorAll('[data-ex-upsell-option]'));
    const chosen = selects.map((s) => s.value);
    selects.forEach((s, idx) => {
      Array.from(s.options).forEach((opt) => {
        const ok = variants.some(
          (v) => v.available && v.options[idx] === opt.value && v.options.every((o, i) => i === idx || o === chosen[i])
        );
        opt.disabled = !ok && opt.value !== s.value;
      });
    });
    const match = variants.find((v) => v.options.every((o, i) => o === chosen[i]));
    const label = btn.querySelector('.ex-upsell__btn-label');
    if (!btn.dataset.label && label) btn.dataset.label = label.innerHTML;
    if (match && match.available) {
      btn.dataset.variantId = match.id;
      btn.disabled = false;
      if (label && btn.dataset.label) label.innerHTML = btn.dataset.label;
      card.querySelectorAll('[data-ex-upsell-price], [data-ex-upsell-btn-price]').forEach((el) => {
        el.innerHTML = Elexa.money(match.price);
      });
      const compare = card.querySelector('[data-ex-upsell-compare]');
      if (compare) {
        compare.hidden = !(match.compare > match.price);
        compare.innerHTML = Elexa.money(match.compare);
      }
    } else {
      btn.disabled = true;
      if (label) label.textContent = str('unavailable', 'Unavailable');
    }
  }

  function syncAllUpsells() {
    document.querySelectorAll('[data-ex-upsell]').forEach(syncUpsell);
  }

  function upsellState(btn, kind, text) {
    if (!btn.isConnected) return;
    btn.classList.remove('is-loading');
    btn.classList.add(`is-${kind}`);
    const stateEl = btn.querySelector('[data-ex-upsell-state]');
    if (stateEl) stateEl.textContent = text;
    setTimeout(() => {
      if (!btn.isConnected) return;
      btn.classList.remove(`is-${kind}`);
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }, 2000);
  }

  /* ---------- errors ---------- */
  let errorTimer;
  function showDrawerError(message) {
    const box = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (!box) return;
    box.textContent = message || str('cartError', 'There was an error');
    clearTimeout(errorTimer);
    errorTimer = setTimeout(() => {
      const current = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
      if (current) current.textContent = '';
    }, 6000);
  }
  C.showError = showDrawerError;

  /* ---------- after every render ---------- */
  function postRender(cart) {
    updateProgress(lastQualifying);
    lastQualifying = typeof state.qualifyingCents === 'number' ? state.qualifyingCents : lastQualifying;
    if (cart) renderCodes(cart);
    syncAllUpsells();
    restoreDrawerFocus();
    const drawer = drawerEl();
    if (drawer && state.keepOpen && Elexa.isDesignMode() && !drawer.classList.contains('active')) openDrawer();
    Elexa.emit('elexa:cart-rendered', { state, cart: cart || null }, drawer || document);
  }

  C.hooks.afterRender.push(({ cart }) => postRender(cart));

  /* ------------------------------------------------------------------ *
   * Drawer helpers
   * ------------------------------------------------------------------ */
  function openDrawer() {
    customElements.whenDefined('cart-drawer').then(() => {
      const drawer = drawerEl();
      if (drawer && !drawer.classList.contains('active') && typeof drawer.open === 'function') drawer.open();
    });
  }
  function closeDrawer() {
    const drawer = drawerEl();
    if (drawer && drawer.classList.contains('active') && typeof drawer.close === 'function') drawer.close();
  }

  // First open: hydrate the applied-code pills with Shopify's per-code applicable state.
  let codesHydrated = false;
  document.addEventListener('elexa:cart-opened', () => {
    if (codesHydrated) return;
    codesHydrated = true;
    if (!document.querySelector('[data-ex-discount-codes]') || !state.itemCount) return;
    (C.lastCart ? Promise.resolve(C.lastCart) : C.get()).then(renderCodes).catch(() => {});
  });

  /* ------------------------------------------------------------------ *
   * Delegated behaviours
   * ------------------------------------------------------------------ */
  // Shipping protection toggle
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ex-protection-toggle]');
    if (!btn) return;
    e.preventDefault();
    if (btn.dataset.busy) return;
    btn.dataset.busy = '1';
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    const on = btn.getAttribute('aria-checked') === 'true';
    const request = on
      ? C.change(btn.dataset.lineKey, 0, { source: 'protection' })
      : C.add([{ id: Number(btn.dataset.variantId), quantity: 1, properties: { _elexa_protection: '1' } }], {
          open: false,
          source: 'protection',
        });
    request.catch((err) => {
      if (btn.isConnected) {
        delete btn.dataset.busy;
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }
      showDrawerError(err && err.message);
    });
  });

  // Cart page: protection summary row remove
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ex-cart-remove-key]');
    if (!btn) return;
    e.preventDefault();
    if (btn.dataset.busy) return;
    btn.dataset.busy = '1';
    C.change(btn.dataset.exCartRemoveKey, 0, { source: 'protection' }).catch((err) => {
      delete btn.dataset.busy;
      showDrawerError(err && err.message);
    });
  });

  // Upsell variant menus
  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-ex-upsell-option]');
    if (!select) return;
    const card = select.closest('[data-ex-upsell]');
    if (card) syncUpsell(card);
  });

  // Upsell add
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ex-upsell-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    C.add([{ id: Number(btn.dataset.variantId), quantity: 1 }], { open: true, source: 'upsell' })
      .then(() => upsellState(btn, 'success', btn.dataset.successLabel || ''))
      .catch((err) => {
        upsellState(btn, 'error', str('error', 'Error'));
        showDrawerError(err && err.message);
      });
  });

  // Discount code form
  const msgTimers = {};
  function discountMessage(blockId, text, kind, keepValue) {
    const block = document.querySelector(`[data-ex-discount][data-block-id="${CSS.escape(blockId)}"]`);
    if (!block) return;
    const msg = block.querySelector('[data-ex-discount-msg]');
    if (msg) {
      msg.textContent = text || '';
      msg.classList.toggle('is-error', kind === 'error');
      msg.classList.toggle('is-success', kind === 'success');
    }
    if (keepValue !== undefined) {
      const input = block.querySelector('[data-ex-discount-input]');
      if (input) input.value = keepValue;
    }
    clearTimeout(msgTimers[blockId]);
    msgTimers[blockId] = setTimeout(() => {
      const current = document.querySelector(`[data-ex-discount][data-block-id="${CSS.escape(blockId)}"] [data-ex-discount-msg]`);
      if (current) {
        current.textContent = '';
        current.classList.remove('is-error', 'is-success');
      }
    }, 4000);
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-ex-discount-form]');
    if (!form) return;
    e.preventDefault();
    const block = form.closest('[data-ex-discount]');
    if (!block || block.dataset.busy) return;
    const id = block.dataset.blockId;
    const input = form.querySelector('[data-ex-discount-input]');
    const code = String((input && input.value) || '').trim();
    const d = block.dataset;
    if (!code) return discountMessage(id, d.msgEmpty, 'error');
    if (appliedCodes().some((c) => String(c).toLowerCase() === code.toLowerCase())) {
      return discountMessage(id, d.msgExists, 'error');
    }
    block.dataset.busy = '1';
    const button = form.querySelector('[data-ex-discount-submit]');
    if (button) {
      button.classList.add('is-loading');
      button.disabled = true;
    }
    try {
      const result = await C.applyDiscount(code, { source: 'discount' });
      if (result.applied) {
        discountMessage(id, d.msgSuccess, 'success', '');
      } else {
        // Shopify keeps unknown or inapplicable codes on the cart: take it off again.
        await C.removeDiscount(code, { source: 'discount' }).catch(() => {});
        discountMessage(id, d.msgError, 'error', code);
      }
    } catch (err) {
      discountMessage(id, d.msgError, 'error', code);
    } finally {
      const current = document.querySelector(`[data-ex-discount][data-block-id="${CSS.escape(id)}"]`);
      [block, current].forEach((b) => {
        if (!b) return;
        delete b.dataset.busy;
        const btn = b.querySelector('[data-ex-discount-submit]');
        if (btn) {
          btn.classList.remove('is-loading');
          btn.disabled = false;
        }
      });
    }
  });

  // Applied-code pill remove
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ex-discount-remove]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;
    C.removeDiscount(btn.dataset.exDiscountRemove, { source: 'discount' }).catch((err) => {
      btn.disabled = false;
      showDrawerError(err && err.message);
    });
  });

  /* ------------------------------------------------------------------ *
   * Theme editor
   * ------------------------------------------------------------------ */
  if (Elexa.isDesignMode()) {
    const isDrawerSection = (e) => e.detail && e.detail.sectionId === 'cart-drawer';
    // A setting change re-renders the static section and replaces <cart-drawer>: remember whether it
    // was open so it comes back open (and the page scroll lock is not left behind on a closed drawer).
    let reopenAfterLoad = false;
    document.addEventListener('shopify:section:select', (e) => {
      if (isDrawerSection(e)) openDrawer();
    });
    document.addEventListener('shopify:block:select', (e) => {
      if (!isDrawerSection(e)) return;
      openDrawer();
      // Bring the selected block into view inside the drawer's scroll areas.
      const target = e.target;
      setTimeout(() => {
        if (target && target.isConnected && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ block: 'nearest', behavior: Elexa.reducedMotion() ? 'auto' : 'smooth' });
        }
      }, 350);
    });
    document.addEventListener('shopify:section:deselect', (e) => {
      if (isDrawerSection(e) && !state.keepOpen) closeDrawer();
    });
    document.addEventListener('shopify:section:unload', (e) => {
      if (!isDrawerSection(e)) return;
      const drawer = drawerEl();
      reopenAfterLoad = Boolean(drawer && drawer.classList.contains('active'));
    });
    document.addEventListener('shopify:section:load', (e) => {
      if (!isDrawerSection(e)) return;
      state = readState() || state;
      lastQualifying = state.qualifyingCents || 0;
      updateProgress();
      syncAllUpsells();
      if (state.keepOpen || reopenAfterLoad) openDrawer();
      reopenAfterLoad = false;
    });
  }

  /* ------------------------------------------------------------------ *
   * Init
   * ------------------------------------------------------------------ */
  // Notification mode with upsells: request the drawer section too so the popup list stays fresh.
  if (state.mode === 'notification' && document.getElementById('cart-notification-upsells')) C.sections.push('cart-drawer');

  function init() {
    updateProgress();
    syncAllUpsells();
    const notifTarget = document.getElementById('cart-notification-upsells');
    const tpl = document.getElementById('ExCartNotificationUpsells');
    if (notifTarget && tpl) notifTarget.innerHTML = tpl.innerHTML;
    if (state.keepOpen && Elexa.isDesignMode()) openDrawer();
    initialReconcile();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
